import { Subject, from } from 'rxjs';
import i18next from 'i18next';
import { isBadNetworkError } from 'web-app/util/url';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { addErrorToErrorLog } from 'web-app/react/routes/error-log/helpers';
import { Headers as FamlyHeaders } from '@famly/vet_apollo_client';
import { verifyRequestUUID } from './common';
import { RestClientNetworkError } from './rest-client-network-error';
/**
 * Retry configuration for GET requests.
 * Uses exponential backoff with jitter to prevent thundering herd
 * (See https://encore.dev/blog/thundering-herd-problem#client-changes for a simple explanation of the thundering herd problems and mitigations).
 */
const RETRY_CONFIG = {
    /** Maximum number of attempts */
    maxAttempts: 3,
    /** Initial delay in milliseconds before first retry */
    initialDelayMs: 300,
    /** Maximum delay cap in milliseconds */
    maxDelayMs: 3000,
    /** Whether to add random jitter to prevent thundering herd */
    jitter: true,
};
/**
 * Calculates the delay for a retry attempt using exponential backoff with optional jitter.
 * @param attemptNumber - The current attempt number (1-indexed)
 * @param config - Retry configuration
 * @returns Delay in milliseconds
 */
const calculateRetryDelay = (attemptNumber, config) => {
    // Exponential backoff: initialDelay * 2^(attempt-1)
    const exponentialDelay = config.initialDelayMs * Math.pow(2, attemptNumber - 1);
    const cappedDelay = Math.min(exponentialDelay, config.maxDelayMs);
    if (!config.jitter) {
        return cappedDelay;
    }
    // Add random jitter: delay * random(0.5, 1.5) to spread out retries
    const jitterMultiplier = 0.5 + Math.random();
    return Math.floor(cappedDelay * jitterMultiplier);
};
/**
 * Determines if an error is a server-side issue (HTTP 5xx).
 * @param error - The error to check
 * @returns True if the error is a server-side issue
 */
const isServerSideIssue = (error) => {
    // Check if error is a real object (null check is required as it's also typeof 'object' and the 'in' operator fails on null)
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    // Check for status property
    if (!('status' in error) || typeof error.status !== 'number') {
        return false;
    }
    return error.status >= 500 && error.status < 600;
};
/**
 * Determines if an error is a transient error that should be retried.
 * Retries on network-level failures and HTTP 5xx errors.
 * @param error - The error to check
 * @returns True if the error is retryable
 */
const isRetryableError = (error) => {
    // Network errors (TypeError: Failed to fetch, connection refused, etc.)
    // Syntax errors can also indicate network issues if the response is malformed - e.g., HTML error page instead of JSON
    if (error instanceof TypeError || error instanceof SyntaxError) {
        return true;
    }
    // HTTP 5xx server-side errors
    if (isServerSideIssue(error)) {
        return true;
    }
    return false;
};
const ErrorSubject = new Subject();
const noOpAugment = (url, fetchOptions) => fetcher => fetcher(url, fetchOptions);
const createDispatchRequest = (APIHelper, sentryService, onError, ignoredErrorCodes = [], fetchAugment = noOpAugment) => ({ endpoint, data, method, authenticated, options }) => {
    const { headers: requestHeaders, requestUUID } = APIHelper.getRequestHeaders(authenticated, { route: endpoint });
    const { overrideApiKey, customApiUrl, suppressErrors, ignoredErrors = [], ...otherOptions } = options || {};
    const apiUrl = customApiUrl || (overrideApiKey ? APIHelper.getApiUrlByKey(overrideApiKey) : APIHelper.apiUrl);
    const url = `${apiUrl}/${endpoint}`;
    const isGet = method === 'GET';
    const headers = new Headers(requestHeaders);
    headers.append('Content-Type', 'application/json');
    const logErrorToSentry = (url, error, queryData, settings, sentryService) => {
        var _a, _b;
        if (((_b = (_a = error === null || error === void 0 ? void 0 : error.responseData) === null || _a === void 0 ? void 0 : _a.log) !== null && _b !== void 0 ? _b : true) === true) {
            const cleanedUrl = cleanUrlForLogging(url);
            const { responseData } = error;
            const errorCodeText = hasValue(responseData === null || responseData === void 0 ? void 0 : responseData.errorCode) ? ` - ${responseData.errorCode}` : '';
            const fingerprint = hasValue(responseData === null || responseData === void 0 ? void 0 : responseData.errorCode)
                ? [cleanedUrl, responseData.errorCode]
                : [cleanedUrl];
            sentryService.captureMessage(`${error.statusText} - ${cleanedUrl}${errorCodeText}`, {
                fingerprint,
                extra: {
                    requestUUID: settings.requestUUID,
                    type: settings.method,
                    url,
                    path: cleanedUrl,
                    data: queryData,
                    status: error.status,
                    error: error.statusText,
                    response: responseData,
                },
            });
        }
    };
    const onRequestUUIDError = (requestUUID, responseUUID, identifier) => sentryService.captureMessage('Famly has detected what seems to be an unsafe caching layer', {
        fingerprint: ['1542636641'],
        extra: {
            identifier,
            responseUUID,
            requestUUID,
        },
        level: 'warning',
    });
    const urlObject = new URL(url, APIHelper.baseUrl);
    if (isGet && data) {
        Object.keys(data).forEach(key => key !== 'uuid' && data[key] !== null && urlObject.searchParams.append(key, data[key]));
    }
    const body = isGet ? undefined : JSON.stringify(data);
    const settings = {
        requestUUID,
        timeout: 60 * 1000,
        ...otherOptions,
        method,
        headers,
        body,
        dataType: 'json',
    };
    return fetchAugment(urlObject, settings)((url, settings, augmentCallbacks) => {
        const run = ({ attemptNumber }) => {
            return fetchWithTimeout(url, settings)
                .then(response => {
                // There is no reason to verify requestUUID headers if the response failed
                if (response.ok) {
                    const responseUUID = response.headers.get(FamlyHeaders.XFamlyRequestUuid);
                    verifyRequestUUID(requestUUID, responseUUID, url.href, onRequestUUIDError);
                }
                const baseResponse = {
                    ok: response.ok,
                    status: response.status,
                    statusText: response.statusText,
                };
                // Handle "204 - No Content" status.
                if (response.status === 204) {
                    return baseResponse;
                }
                // Convert the response from stream to readable json.
                // We use .text() first to handle empty response bodies gracefully.
                // This prevents SyntaxError from masking the real HTTP error (e.g., 500 with empty body).
                return response.text().then(text => {
                    const responseData = text ? JSON.parse(text) : undefined;
                    return {
                        ...baseResponse,
                        responseData,
                    };
                });
            })
                .then(response => {
                var _a;
                // Since http error responses (status 400 and above) doesn't go to the catch of a promise we force it here
                if (!response.ok) {
                    throw response;
                }
                (_a = augmentCallbacks === null || augmentCallbacks === void 0 ? void 0 : augmentCallbacks.onSuccess) === null || _a === void 0 ? void 0 : _a.call(augmentCallbacks, response);
                return response.responseData;
            })
                .catch(error => {
                var _a, _b;
                const shouldRetry = isGet &&
                    !settings.skipRetries &&
                    isRetryableError(error) &&
                    attemptNumber <= RETRY_CONFIG.maxAttempts;
                // Retry GET requests on transient network errors with exponential backoff
                if (shouldRetry) {
                    const delay = calculateRetryDelay(attemptNumber, RETRY_CONFIG);
                    console.warn(`GET request to "${cleanUrlForLogging(urlObject.href)}" failed (attempt ${attemptNumber}/${RETRY_CONFIG.maxAttempts + 1}) - retrying in ${delay}ms`);
                    return new Promise(resolve => setTimeout(() => resolve(run({ attemptNumber: attemptNumber + 1 })), delay));
                }
                console.error('Error when calling URL:', urlObject);
                console.error('The error was:', error);
                // Put the error in the global error stream so you can do global error handling
                ErrorSubject.next(error);
                (_a = augmentCallbacks === null || augmentCallbacks === void 0 ? void 0 : augmentCallbacks.onError) === null || _a === void 0 ? void 0 : _a.call(augmentCallbacks, error);
                // The error code is set in responses from core & famlyapi
                const errorCode = error.errorCode || (error.responseData && error.responseData.errorCode);
                if (!(suppressErrors || ignoredErrors.includes(errorCode))) {
                    globalEventOnError(error, onError, urlObject.href);
                }
                // Fetch returns an error object if there is a network error, otherwise the response from the http error
                if (error instanceof Error) {
                    addErrorToErrorLog({
                        type: 'network-error',
                        name: error.name,
                        message: error.message,
                        restRoute: urlObject.pathname,
                    });
                    if (error instanceof SyntaxError) {
                        // Non-JSON response body (e.g. HTML error page from a proxy/gateway).
                        // globalEventOnError above already shows the nonJSONResponse message to the user.
                        // We throw a descriptive RestClientNetworkError instead of re-throwing the SyntaxError,
                        // which would appear in Sentry as "Unexpected token '<'" with no useful context.
                        throw new RestClientNetworkError(`Non-JSON response body when calling ${cleanUrlForLogging(urlObject.href)}`, undefined);
                    }
                    throw error;
                }
                else {
                    const responseData = error.responseData;
                    addErrorToErrorLog({
                        type: 'rest-error',
                        code: `${errorCode}`,
                        name: 'REST error',
                        message: responseData === null || responseData === void 0 ? void 0 : responseData.error,
                        statusCode: responseData === null || responseData === void 0 ? void 0 : responseData.statusCode,
                        restRoute: urlObject.pathname,
                    });
                    if (!ignoredErrorCodes.includes(errorCode)) {
                        logErrorToSentry(urlObject.href, error, data, settings, sentryService);
                    }
                    const path = cleanUrlForLogging(urlObject.href);
                    const statusCode = (_b = error === null || error === void 0 ? void 0 : error.status) !== null && _b !== void 0 ? _b : 'Unknown';
                    throw new RestClientNetworkError(`Network error (${statusCode}) when calling ${path}: ${errorCode}`, error === null || error === void 0 ? void 0 : error.responseData);
                }
            });
        };
        return run({ attemptNumber: 1 });
    });
};
export const cleanUrlForLogging = (url) => {
    const [cleanedUrl] = url
        // We replace all UUIDs and ULIDs with a placeholder to get the same fingerprint for all errors
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '{uuid}')
        .replace(/[0-7][0-9A-HJKMNP-TV-Z]{25}/, '{ulid}')
        // We also remove the domain and api prefix
        .replace(/https?:\/\/[^/]+(?:\/api)?/gi, '')
        .split('?');
    return cleanedUrl;
};
const globalEventOnError = (error, onError, url) => {
    var _a, _b, _c;
    if (error instanceof SyntaxError) {
        // If the backend respons with a non-JSON response, we show a better-looking error message.
        // Otherwise error message would be something like "Unexpected token < at position 0"
        onError(i18next.t($ => $.network.specialCaseErrors.nonJSONResponse), {
            checkConnection: false,
            url,
            error,
        });
    }
    else if (error instanceof Error) {
        if (isBadNetworkError(error)) {
            onError(i18next.t($ => $.network.specialCaseErrors.badNetworkError), {
                checkConnection: false,
                url,
                error,
            });
        }
        else {
            // Fetch returns an error object if there is a network error, otherwise the response from the http error
            onError(i18next.t($ => $.network.checkConnection), { checkConnection: true, url, error });
        }
    }
    else {
        onError((_c = (_b = (_a = error.responseData) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : error.statusText) !== null && _c !== void 0 ? _c : 'Unknown error');
    }
};
const sanitizeGetData = (data) => {
    if (data) {
        return Object.keys(data).reduce((newObject, current) => {
            const currentValue = data[current];
            if (currentValue !== '' && currentValue !== undefined) {
                newObject[current] = currentValue;
            }
            return newObject;
        }, {});
    }
    return {};
};
const fetchWithTimeout = (url, allOptions) => {
    const { timeout, ...options } = allOptions;
    const controller = new AbortController();
    const signal = controller.signal;
    const timer = setTimeout(() => {
        return controller.abort(new Error('Fetch request aborted due to timeout'));
    }, timeout);
    // So. Apparently finally is not part of the fetch spec since it is not part of the promise spec
    // To the get Edge to work with finally we need to wrap the fetch in a Promise that has the promise finally polyfill
    const promise = new Promise((resolve, reject) => fetch(url, {
        signal,
        credentials: 'same-origin',
        ...options,
    }).then(resolve, reject));
    return promise.finally(() => {
        return clearTimeout(timer);
    });
};
export const createRestClient = (APIHelper, onError, sentryService, ignoredErrorCodes = [], fetchAugment) => {
    const dispatchRequest = createDispatchRequest(APIHelper, sentryService, onError, ignoredErrorCodes, fetchAugment);
    const dispatchToAllAPIs = ({ endpoint, data, method, options, authenticated }) => {
        const { stopErrorCode, ...otherOptions } = options || {};
        const dispatchToAPIs = (apiKeys) => {
            const [apiKey, ...extraKeys] = apiKeys;
            return dispatchRequest({
                endpoint,
                data,
                method,
                options: {
                    timeout: 15 * 1000,
                    ...otherOptions,
                    suppressErrors: true,
                    overrideApiKey: apiKey,
                },
                authenticated,
            })
                .then(data => {
                APIHelper.setApiConfig(apiKey);
                return data;
            })
                .catch(e => {
                const errorCode = e.errorCode || (e.responseData && e.responseData.errorCode);
                if (ignoredErrorCodes.includes(errorCode)) {
                    throw e;
                }
                if (stopErrorCode && e.errorCode === stopErrorCode && extraKeys[0]) {
                    if (!otherOptions.suppressErrors) {
                        onError(e.error);
                    }
                    throw e;
                }
                else if (extraKeys[0]) {
                    return dispatchToAPIs(extraKeys);
                }
                else if (!otherOptions.suppressErrors) {
                    if (e.statusCode === 0) {
                        onError(i18next.t($ => $.network.checkConnection), {
                            checkConnection: true,
                            url: endpoint,
                            error: e,
                        });
                    }
                    else if (e.error) {
                        onError(e.error);
                    }
                }
                throw e;
            });
        };
        return dispatchToAPIs(APIHelper.apiKeys);
    };
    const dispatchAuthenticatedRequest = ({ endpoint, data, method, options }) => {
        return dispatchRequest({
            options,
            endpoint,
            data,
            method,
            authenticated: true,
        });
    };
    const dispatchAuthenticatedToAllAPIs = ({ endpoint, data, method, options }) => {
        return dispatchToAllAPIs({
            endpoint,
            data,
            method,
            options,
            authenticated: true,
        });
    };
    const post = (endpoint, data, options) => {
        return dispatchRequest({
            options,
            endpoint,
            data,
            method: 'POST',
        });
    };
    const postAuthenticated = (endpoint, data, options) => {
        return dispatchAuthenticatedRequest({
            options,
            endpoint,
            data,
            method: 'POST',
        });
    };
    const postObservable = (endpoint, data, options) => {
        return from(post(endpoint, data, options));
    };
    const postAuthenticatedObservable = (endpoint, data, options) => {
        return from(postAuthenticated(endpoint, data, options));
    };
    const postObservableToAllAPIs = (endpoint, data, options) => {
        return from(postToAllAPIs(endpoint, data, options));
    };
    const postToAllAPIs = (endpoint, data, options) => {
        return dispatchToAllAPIs({
            endpoint,
            data,
            options,
            method: 'POST',
        });
    };
    const postAuthenticatedToAllAPIs = (endpoint, data, options) => {
        return dispatchAuthenticatedToAllAPIs({
            endpoint,
            data,
            options,
            method: 'POST',
        });
    };
    const putAuthenticated = (endpoint, data, options) => {
        return dispatchAuthenticatedRequest({
            options,
            endpoint,
            data,
            method: 'PUT',
        });
    };
    const putAuthenticatedObservable = (endpoint, data, options) => {
        return from(putAuthenticated(endpoint, data, options));
    };
    const deleteAuthenticated = (endpoint, data, options) => {
        return dispatchAuthenticatedRequest({
            options,
            endpoint,
            data,
            method: 'DELETE',
        });
    };
    const deleteAuthenticatedObservable = (endpoint, data, options) => {
        return from(deleteAuthenticated(endpoint, data, options));
    };
    const getRequest = (endpoint, data, options) => {
        return dispatchRequest({
            options,
            endpoint,
            data: sanitizeGetData(data),
            method: 'GET',
        });
    };
    const getAuthenticated = (endpoint, data, options) => {
        return dispatchAuthenticatedRequest({
            options,
            endpoint,
            data: sanitizeGetData(data),
            method: 'GET',
        });
    };
    const getObservable = (endpoint, data, options) => {
        return from(getRequest(endpoint, data, options));
    };
    const getAuthenticatedObservable = (endpoint, data, options) => {
        return from(getAuthenticated(endpoint, data, options));
    };
    const dispatchAuthenticatedRequestObservable = ({ endpoint, data, method, options }) => {
        return from(dispatchAuthenticatedRequest({
            options,
            endpoint,
            data,
            method,
        }));
    };
    return {
        post,
        postAuthenticated,
        postObservable,
        postAuthenticatedObservable,
        postToAllAPIs,
        postAuthenticatedToAllAPIs,
        postObservableToAllAPIs,
        putAuthenticated,
        putAuthenticatedObservable,
        deleteAuthenticated,
        deleteAuthenticatedObservable,
        getRequest,
        getAuthenticated,
        getObservable,
        getAuthenticatedObservable,
        dispatchAuthenticatedRequestObservable,
        ErrorSubject,
    };
};
